import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ErrorCode } from '@repo/types'

import { createApiCall } from 'services'

import { MY_PENDING_SALES_QUERY_KEY } from './useMyPendingSales'

/**
 * What the buyer's scan produced. EITHER is enough, and the API accepts either.
 *
 * `tagUid` is the scan route proper and is the only one that can report "this
 * tag has no certificate" (a chip encoded into a box that was never bound to a
 * product). `certificateId` is what an iOS tap yields, because the OS opens the
 * tag's URL itself and the app is re-entered with the certificate already
 * resolved. That failure is unrepresentable on the certificate route, which is
 * why the API keeps both.
 */
export interface PendingSaleScanResult {
  tagUid?: string
  certificateId?: string
}

export interface ResolvePendingSaleSuccess {
  pending_ownership_id: string
  certificate_id: string
  transfer_id: string | null
  already_owned: boolean
}

/**
 * A typed failure, carried as a value rather than thrown as a string.
 *
 * Each code needs a different thing said to the buyer, and several offer
 * actions. A rejected promise carrying a message would force the UI to match on
 * prose, which is how a copy edit silently becomes a behaviour change.
 */
export class PendingSaleResolveError extends Error {
  readonly code: ErrorCode | null
  readonly status: number | null

  constructor(code: ErrorCode | null, status: number | null, message: string) {
    super(message)
    this.name = 'PendingSaleResolveError'
    this.code = code
    this.status = status
  }
}

const resolvePendingSale = async (
  scan: PendingSaleScanResult,
): Promise<ResolvePendingSaleSuccess> => {
  const response = await createApiCall({
    method: 'POST',
    url: '/pending-ownership/customer/resolve',
    data: {
      tag_uid: scan.tagUid,
      certificate_id: scan.certificateId,
    },
  })

  const status = (response as { status?: number } | undefined)?.status

  if (typeof status !== 'number' || status < 200 || status >= 300) {
    const body = (response as { data?: { code?: ErrorCode; message?: string } } | undefined)?.data

    throw new PendingSaleResolveError(
      body?.code ?? null,
      typeof status === 'number' ? status : null,
      // Kept for logs and for the last-resort branch in the copy map. It is
      // NEVER rendered verbatim: a buyer must not read a server string, and
      // must never read a raw error code.
      body?.message || 'Resolve failed',
    )
  }

  return response.data as ResolvePendingSaleSuccess
}

/**
 * VRG-455 — bind the certificate of the tag the buyer just scanned to the sale
 * the brand recorded for them, completing the transfer at `scan_claimed`.
 *
 * The scan is LOAD-BEARING and this hook is the only place that shows it. The
 * pending row cannot complete a claim on its own, because nothing on that page
 * knows which physical item the buyer is holding — the whole point of the
 * design is that nobody guessed.
 */
export const useResolvePendingSale = () => {
  const queryClient = useQueryClient()

  return useMutation<ResolvePendingSaleSuccess, PendingSaleResolveError, PendingSaleScanResult>({
    mutationFn: resolvePendingSale,
    onSuccess: (data) => {
      // The record is consumed, so it leaves the pending list...
      queryClient.invalidateQueries({ queryKey: MY_PENDING_SALES_QUERY_KEY })
      // ...and the certificate it bound arrives in the certificates list. Both
      // halves matter: refreshing only one leaves the buyer looking at an item
      // that is simultaneously pending and owned.
      queryClient.invalidateQueries({ queryKey: ['certificates'] })
      queryClient.invalidateQueries({
        queryKey: ['certificate-public', data.certificate_id],
      })
    },
  })
}
